import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux'; // useSelector ยังใช้สำหรับ info
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Autocomplete,
  TextField,
  Stack,
  Divider,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
} from '@mui/material';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SaveIcon from '@mui/icons-material/Save';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import EmployeeAvatar from '../../components/EmployeeAvatar';
import { APPROVER_SLOT_COUNT, getExpenseApproverSlots } from '../../utils/functions/approverAccess';

const PAGE_SIZE = 50;

function EmployeeAutocomplete({ value, onChange, color, placeholder }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  const hasMore = rows.length < total;

  const load = useCallback(async (p, q, reset) => {
    setLoading(true);
    try {
      const { data, status } = await api.get(
        `${process.env.REACT_APP_API_URL}/employee?name=${q}&size=${PAGE_SIZE}&page=${p}`,
      );
      if (status === 200) {
        setTotal(data?.total || 0);
        setRows((prev) => reset ? (data?.rows || []) : [...prev, ...(data?.rows || [])]);
        setPage(p);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // โหลดครั้งแรกตอน mount
  useEffect(() => { load(1, '', true); }, []);

  const handleInputChange = (_, val, reason) => {
    if (reason !== 'input') return;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(1, val, true), 300);
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (!hasMore || loading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
      load(page + 1, search, false);
    }
  };

  return (
    <Autocomplete
      value={value}
      options={rows}
      getOptionLabel={(o) =>
        typeof o === 'string' ? o : `${formatEmployeeThaiName(o)} (${o?.employee_id || ''})`
      }
      isOptionEqualToValue={(o, v) => o?._id === v?._id}
      onChange={(_, v) => onChange(v)}
      filterOptions={(x) => x}
      onInputChange={handleInputChange}
      loading={loading}
      ListboxProps={{ onScroll: handleScroll, style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': { borderColor: color },
              '&.Mui-focused fieldset': { borderColor: color },
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option._id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmployeeAvatar employee={option} size={28} />
            <Box>
              <Typography variant="body2">{formatEmployeeThaiName(option)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option?.employee_id} · {option?.department?.name || '-'}
              </Typography>
            </Box>
          </Box>
        </li>
      )}
    />
  );
}

export default function HrExpenseApproverSettings({ title, subtitle }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const info = useSelector((state) => state.info);


  const [approvalMode, setApprovalMode] = useState('THREE_STEP'); // ONE_STEP | THREE_STEP
  // expense_approver_1..10 — ONE_STEP ใช้ครบ 10 ช่อง (ใครก็ได้ในรายการ เหมือน /management/approvers),
  // THREE_STEP ใช้แค่ 3 ช่องแรกตามลำดับขั้น
  const [approvers, setApprovers] = useState(() => Array(APPROVER_SLOT_COUNT).fill(null));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(actions.getInformation({}));
  }, []);

  // Sync local state from info when it loads
  useEffect(() => {
    if (info?._id) {
      setApprovers(getExpenseApproverSlots(info).map((a) => a || null));
      setApprovalMode(info?.expense_approval_mode || 'THREE_STEP');
    }
  }, [info?._id]);

  const setApproverAt = (idx) => (value) => setApprovers((prev) => prev.map((a, i) => (i === idx ? value : a)));

  const handleSave = async () => {
    const confirm = await confirmAsync('บันทึกการตั้งค่าผู้อนุมัติ?');
    if (!confirm) return;
    setSaving(true);
    try {
      await dispatch(
        actions.editOneInformation(info?._id, {
          expense_approval_mode: approvalMode,
          // เก็บครบทุกช่องเสมอ (สลับโหมดไปมาแล้วช่อง 4-10 ไม่หาย) — THREE_STEP ไม่นับช่อง 4-10 ตอนเช็คสิทธิ์
          ...Object.fromEntries(approvers.map((a, i) => [`expense_approver_${i + 1}`, a?._id || null])),
        }),
      );
      await dispatch(actions.getInformation({}));
      showAlert('บันทึกสำเร็จ');
    } catch (err) {
      console.error(err);
      showAlert('เกิดข้อผิดพลาด', { status: err?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const isOneStep = approvalMode === 'ONE_STEP';

  // 3 ช่องแรกคงไอคอน/สีเดิม ช่อง 4-10 (เฉพาะ ONE_STEP) ใช้เลขในกล่องสี primary
  const STEP_ORDINAL = ['1st', '2nd', '3rd'];
  const FIRST_THREE = [
    { icon: <LooksOneIcon />, color: theme.palette.warning.main },
    { icon: <LooksTwoIcon />, color: theme.palette.info.main },
    { icon: <Looks3Icon />, color: theme.palette.success.main },
  ];
  const steps = approvers.map((value, i) => ({
    label: isOneStep
      ? `ผู้อนุมัติคนที่ ${i + 1} (ใครก็ได้ในรายการนี้)`
      : `ผู้อนุมัติขั้นที่ ${i + 1} (${STEP_ORDINAL[i]} Approver)`,
    icon: FIRST_THREE[i]?.icon || (
      <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{i + 1}</Typography>
    ),
    color: FIRST_THREE[i]?.color || theme.palette.primary.main,
    value,
    onChange: setApproverAt(i),
  }));

  // ONE_STEP แสดงครบ 10 ช่อง (ใครคนใดคนหนึ่งก็ได้), THREE_STEP แสดง 3 ขั้น
  const visibleSteps = isOneStep ? steps : steps.slice(0, 3);
  const filledCount = approvers.filter(Boolean).length;


  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <HowToRegIcon color="primary" sx={{ fontSize: 28 }} />
        <Box>
          <Typography variant="h6" fontWeight={700}>
            ตั้งค่าผู้อนุมัติรายการเบิกเงิน
          </Typography>
          <Typography variant="body2" color="text.secondary">
            กำหนดพนักงานที่มีสิทธิ์อนุมัติรายการตั้งเบิกในแต่ละขั้นตอน
          </Typography>
        </Box>
      </Box>

      <Card
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {/* Mode selector: 1-step vs 3-step */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ minWidth: 140 }}>
              รูปแบบการอนุมัติ:
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant={isOneStep ? 'contained' : 'outlined'}
                size="small"
                color="primary"
                onClick={() => setApprovalMode('ONE_STEP')}
              >
                1 Step
              </Button>
              <Button
                variant={!isOneStep ? 'contained' : 'outlined'}
                size="small"
                color="primary"
                onClick={() => setApprovalMode('THREE_STEP')}
              >
                3 Steps
              </Button>
            </Stack>
          </Box>

          {/* Flow diagram hint */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              flexWrap: 'wrap',
            }}
          >
            {isOneStep ? (
              <>
                <Chip
                  label={`ผู้อนุมัติคนใดก็ได้ในรายการ · เลือกแล้ว ${filledCount}/${APPROVER_SLOT_COUNT} คน`}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.15),
                    color: theme.palette.warning.main,
                    fontWeight: 600,
                  }}
                />
                <Typography color="text.disabled" fontSize={18}>→</Typography>
                <Chip label="โอนเงิน" size="small" color="success" />
              </>
            ) : (
              <>
                {visibleSteps.map((step, idx) => (
                  <React.Fragment key={idx}>
                    <Chip
                      icon={step.icon}
                      label={`Step ${idx + 1}`}
                      size="small"
                      sx={{
                        bgcolor: alpha(step.color, 0.15),
                        color: step.color,
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: step.color },
                      }}
                    />
                    {idx < visibleSteps.length - 1 && (
                      <Typography color="text.disabled" fontSize={18}>→</Typography>
                    )}
                  </React.Fragment>
                ))}
                <Typography color="text.disabled" fontSize={18}>→</Typography>
                <Chip label="โอนเงิน" size="small" color="success" />
              </>
            )}
          </Box>

          <Stack spacing={3}>
            {visibleSteps.map((step, idx) => (
              <Box key={idx}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: alpha(step.color, 0.12),
                      color: step.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {step.label}
                  </Typography>
                  {step.value && (
                    <Chip
                      size="small"
                      avatar={
                        <EmployeeAvatar employee={step.value} size={24} />
                      }
                      label={formatEmployeeThaiName(step.value)}
                      variant="outlined"
                      sx={{ borderColor: step.color, ml: 'auto' }}
                    />
                  )}
                </Box>
                <EmployeeAutocomplete
                  value={step.value}
                  onChange={step.onChange}
                  color={step.color}
                  placeholder="เลือกพนักงาน..."
                />
                {idx < visibleSteps.length - 1 && <Divider sx={{ mt: 3 }} />}
              </Box>
            ))}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ minWidth: 140, borderRadius: 2 }}
            >
              {saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
